import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';

import { Level } from '@sorare/core/src/contexts/snackNotification';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import {
  errorCodes,
  useMutation,
} from '@sorare/core/src/hooks/graphql/useMutation';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { useGetInteractionContext } from '@sorare/core/src/hooks/useGetInteractionContext';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { notificationsQueue } from '@sorare/core/src/hooks/useNotificationsQueue';

import { RevealPackCardsDialog } from '#src/components/commonCard/RevealPackCardsDialog/index.tsx';

import type {
  useBuyCardPackMutation,
  useBuyCardPackMutationVariables,
} from './__generated__/useBuyCardPack.graphql.ts';

const BUY_CARD_PACK_MUTATION = gql`
  mutation useBuyCardPackMutation($input: buyCardPackInput!, $sport: Sport!) {
    buyCardPack(input: $input) {
      currentUser {
        slug
        gemsBalances: inGameCurrencyBalances(
          sport: $sport
          inGameCurrencies: [COMMON_GEM]
        ) {
          amount
        }
      }
      pack {
        id
        cardPack {
          slug
          maxPurchasePerPack
          userPurchaseCount
          cardPackGroup {
            id
            slug
            boughtPacksCountTask {
              id
              progress
              target
              aasmState
            }
          }
        }
      }
      cards {
        slug
        ...RevealPackCardsDialog_anyCardInterface
      }
      errors {
        code
        message
      }
    }
  }
  ${RevealPackCardsDialog.fragments.anyCardInterface}
` as TypedDocumentNode<useBuyCardPackMutation, useBuyCardPackMutationVariables>;

const isSoldOutError = (errors: readonly { code?: number }[]) =>
  errors.some(error => error.code === errorCodes.cardPackSoldOut);

export const useBuyCardPack = (cardPackSlug: Nullable<string>) => {
  const {
    flags: { disableNativeIOSPackOpening = false },
  } = useFeatureFlags();

  const { sport } = useSportContext();
  const { isIosApp, postMessage } = useIsMobileApp();
  const getInteractionContext = useGetInteractionContext();
  const [buyCardPackData, setBuyCardPackData] =
    useState<useBuyCardPackMutation['buyCardPack']>();
  const [revealOpen, setRevealOpen] = useState(false);
  const [soldOut, setSoldOut] = useState(false);

  const useNativeIOSPackOpening = isIosApp && !disableNativeIOSPackOpening;

  const [buyCardPack, { loading }] = useMutation(BUY_CARD_PACK_MUTATION);

  const onPurchase = async () => {
    if (!cardPackSlug) {
      return;
    }
    const { data, errors } = await buyCardPack({
      variables: {
        input: {
          cardPackSlug,
          sport,
          interactionContext: getInteractionContext(),
        },
        sport,
      },
    });
    if (errors.length > 0) {
      if (isSoldOutError(errors)) {
        setSoldOut(true);
        return;
      }
      notificationsQueue.addNotification(
        'errors',
        { errors: errors.map(error => error.message).join(', ') },
        { level: Level.ERROR }
      );
      return;
    }
    setBuyCardPackData(data?.buyCardPack);
    setRevealOpen(true);
  };

  const onCloseSoldOut = () => {
    setSoldOut(false);
  };

  const onMobilePurchase = () => {
    if (!cardPackSlug) {
      return;
    }
    postMessage('buyCardPack', {
      packId: cardPackSlug,
    });
  };
  const packNotBought =
    buyCardPackData?.cards?.length === 0 || !buyCardPackData?.pack?.id;

  const onCloseRevealDialog = () => {
    setRevealOpen(false);
  };
  const onRevealDialogClosed = () => {
    setBuyCardPackData(undefined);
  };
  return {
    buyCardPackData,
    revealOpen,
    onPurchase,
    onMobilePurchase,
    useNativeIOSPackOpening,
    packNotBought,
    onCloseRevealDialog,
    onRevealDialogClosed,
    soldOut,
    onCloseSoldOut,
    loading,
  };
};
